import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, Optional, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { environment } from "environments/environment";
import _ from "lodash";
import { firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CancellationReasonTypeEnum, ItemDetailStatusEnum } from "@/model/entities/enums";
import { ItemDetail } from "@/model/entities/item-detail";
import { JoDetail } from "@/model/entities/jo-detail";
import { JoHeader } from "@/model/entities/jo-header";
import { JoStatusCode } from "@/model/enums/jo-status-code";
import { BarcodeService } from "@/shared/barcode.service";
import { DomainBaseComponent, wrapBusy } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { UtilFns } from "@/shared/util-fns";
import { OrderFacilitationManagerComponent } from "./order-facilitation-manager.component";

/**
 * Per-line editable state. checked/cancelQty/reasonId/explanation/isOverride
 * are the form fields. joDetail + orderQty/alreadyCancelled/shippedQty/
 * cancellableCeiling are read-only line context.
 */
interface CancellableLineRow {
  joDetail: JoDetail;
  orderQty: number;
  alreadyCancelled: number;
  shippedQty: number;
  cancellableCeiling: number;
  overrideCap: number;

  // Editable form state
  checked: boolean;
  cancelQty: number | null;
  reasonId: CancellationReasonTypeEnum | null;
  explanation: string;
  isOverride: boolean;
}

interface ReasonOption {
  id: CancellationReasonTypeEnum;
  label: string;
  variant: "request" | "definitive";
}

@Component({
  selector: "app-cancel-line",
  templateUrl: "./cancel-line.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CancelLineComponent extends DomainBaseComponent {
  @ViewChild("joSearchInput") joSearchInput?: ElementRef<HTMLInputElement>;

  // Step 1: J/O search
  joNumberInput = "";
  selectedJoHeader: JoHeader | null = null;

  // Step 2: cancellable-lines batch
  cancellableRows: CancellableLineRow[] = [];

  // Reasons + P3-variant hint driven off the selection.
  readonly reasonOptions: ReasonOption[] = [
    { id: CancellationReasonTypeEnum.NoLongerNeeded, label: "No longer needed (customer)", variant: "request" },
    { id: CancellationReasonTypeEnum.OrderedInError, label: "Ordered in error (internal)", variant: "request" },
    { id: CancellationReasonTypeEnum.ManufacturerDiscontinued, label: "Manufacturer discontinued", variant: "definitive" },
    { id: CancellationReasonTypeEnum.ManufacturerConfirmedUnable, label: "Manufacturer confirmed unable", variant: "definitive" },
    { id: CancellationReasonTypeEnum.MigrationCleanup, label: "Migration cleanup", variant: "request" },
    { id: CancellationReasonTypeEnum.Other, label: "Other", variant: "request" },
  ];

  constructor(
    protected override domainService: DomainService,
    protected route: ActivatedRoute,
    private http: HttpClient,
    private barcodeService: BarcodeService,
    @Optional() private orderFacilitationManager: OrderFacilitationManagerComponent,
  ) {
    super(domainService);
    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());
  }

  async updateFromContext() {
    this.isPageReady = true;
    await UtilFns.wait(1);
    this.joSearchInput?.nativeElement?.focus();
  }

  // ---------- Step 1: find the J/O ----------

  onJoNumberInputChange(value: string) {
    if ((value ?? "").length === 7) {
      this.findJoHeader();
    }
  }

  @wrapBusy
  async findJoHeader() {
    this.resetSelection();
    const idText = (this.joNumberInput || "").trim();
    if (!idText) {
      this.toastr.error("Enter a J/O number.");
      return;
    }
    const joId = Number.parseInt(idText, 10);
    if (!Number.isFinite(joId)) {
      this.toastr.error("J/O number must be numeric.");
      return;
    }

    const q = this.uow
      .createQuery(JoHeader, "JoHeaders")
      .where({ id: joId })
      .expand([
        "account",
        "joDetails.product.productType.manufacturer",
        "joDetails.itemDetails",
        "joDetails.cancellationRequestDetails",
      ]);
    const r = await q.execute();
    const joh = _.first(r);
    if (!joh) {
      this.toastr.error(`J/O ${joId} not found.`);
      return;
    }

    if (joh.joStatusId !== JoStatusCode.OpenProcessComplete && joh.joStatusId !== JoStatusCode.OpenProcessIncomplete) {
      this.toastr.error(`Job order ${joId} is not open. You can cancel only an open job order. Enter a different number.`);
      return;
    }

    this.selectedJoHeader = joh;
    this.cancellableRows = this.computeCancellableRows(joh);
    if (this.cancellableRows.length === 0) {
      this.toastr.error(`J/O ${joId} has no cancellable order details.`);
    }
  }

  private computeCancellableRows(joh: JoHeader): CancellableLineRow[] {
    return (joh.joDetails ?? [])
      .map((jod) => {
        const orderQty = jod.orderQty ?? 0;
        const alreadyCancelled = jod.cancelQty ?? 0;
        const shippedQty = (jod.itemDetails ?? []).filter(
          (itd) => itd.itemDetailStatusId === ItemDetailStatusEnum.Shipped,
        ).length;
        const cancellableCeiling = orderQty - alreadyCancelled - shippedQty;
        const overrideCap = orderQty - alreadyCancelled;
        return {
          joDetail: jod,
          orderQty,
          alreadyCancelled,
          shippedQty,
          cancellableCeiling,
          overrideCap,
          checked: false,
          cancelQty: null,
          reasonId: null,
          explanation: "",
          isOverride: false,
        };
      })
      .filter((r) => r.cancellableCeiling > 0 || r.overrideCap > r.cancellableCeiling)
      // ↑ include rows where override could still unlock cancellation (e.g. fully shipped).
      // cancellableCeiling > 0 covers the normal case; the OR handles shipped-only lines.
      .sort((a, b) => a.joDetail.id - b.joDetail.id);
  }

  // ---------- Step 2: per-row edit + submit ----------

  /** When a row is checked, default cancelQty to its full effective ceiling. */
  onRowCheckedChange(row: CancellableLineRow) {
    if (row.checked && row.cancelQty == null) {
      row.cancelQty = this.effectiveCeiling(row);
    }
  }

  /** When override flips on a row, re-clamp cancelQty to the new ceiling if it now exceeds. */
  onRowOverrideChange(row: CancellableLineRow) {
    const eff = this.effectiveCeiling(row);
    if (row.cancelQty != null && row.cancelQty > eff) {
      row.cancelQty = eff;
    }
  }

  effectiveCeiling(row: CancellableLineRow): number {
    return row.isOverride ? row.overrideCap : row.cancellableCeiling;
  }

  isOverrideAvailable(row: CancellableLineRow): boolean {
    return row.overrideCap > row.cancellableCeiling;
  }

  getReasonOption(row: CancellableLineRow): ReasonOption | null {
    if (row.reasonId == null) return null;
    return this.reasonOptions.find((r) => r.id === row.reasonId) ?? null;
  }

  isRowValid(row: CancellableLineRow): boolean {
    if (!row.checked) return true;   // unchecked rows don't need to validate
    if (row.cancelQty == null || row.cancelQty <= 0) return false;
    if (row.cancelQty > this.effectiveCeiling(row)) return false;
    if (row.reasonId == null) return false;
    if (row.isOverride && !(row.explanation ?? "").trim()) return false;
    return true;
  }

  get checkedRows(): CancellableLineRow[] {
    return this.cancellableRows.filter((r) => r.checked);
  }

  get totalUnitsToCancel(): number {
    return this.checkedRows.reduce((sum, r) => sum + (r.cancelQty ?? 0), 0);
  }

  get canSubmit(): boolean {
    if (this.checkedRows.length === 0) return false;
    return this.checkedRows.every((r) => this.isRowValid(r));
  }

  @wrapBusy
  async submit() {
    if (!this.canSubmit) return;
    const rows = this.checkedRows;

    const lines = rows.map((r) => {
      const opt = this.getReasonOption(r)!;
      const parts = [
        `  • Detail ${r.joDetail.id}: cancel ${r.cancelQty} of ${r.orderQty} · reason: ${opt.label}`,
      ];
      if (this.selectedJoHeader?.joStatusId === JoStatusCode.OpenProcessIncomplete) {
        parts.push("    (The pull is not complete. The system sends no email to the manufacturer.)");
      } else if (opt.variant === "request") parts.push("    (manufacturer email will be queued)");
      else parts.push("    (definitive — no manufacturer email)");
      if (r.isOverride) parts.push(`    ⚠ CEILING OVERRIDE — normal ceiling was ${r.cancellableCeiling}`);
      return parts.join("\n");
    });

    const unitsWord = this.totalUnitsToCancel === 1 ? "unit" : "units";
    const detailsWord = rows.length === 1 ? "detail" : "details";
    const confirmMsg =
      `Cancel ${this.totalUnitsToCancel} ${unitsWord} across ${rows.length} order ${detailsWord}?\n\n` +
      lines.join("\n\n");
    const confirmed = await this.dialogService.yesNoDialog("Confirm Cancel Order Details", confirmMsg);
    if (!confirmed) return;

    const actor = this.authUser?.initials ?? "";
    const url = environment.apiRoot + "breeze/Uniformax/CancelLineBatch";
    try {
      const result = await firstValueFrom(
        this.http.post<any>(url, {
          Actor: actor,
          Details: rows.map((r) => ({
            JoDetailId: r.joDetail.id,
            CancelQty: r.cancelQty,
            ReasonId: r.reasonId,
            Explanation: r.explanation || null,
            IsOverride: r.isOverride,
          })),
        }),
      );
      const results = result?.Results ?? [];
      const totalReleased = results.reduce((s: number, x: any) => s + (x?.ReleasedItemDetailIds?.length ?? 0), 0);
      const totalPo = results.reduce((s: number, x: any) => s + (x?.AffectedPoDetailIds?.length ?? 0), 0);
      const totalNotYetOrdered = _.sumBy(results, (x: any) => x?.NotYetOrderedQty ?? 0);

      // Released ItemDetails are indistinguishable from fresh inventory after
      // the server-side reset — they need new barcode labels so the physical
      // units on the shelf can be relabeled. Re-fetch and hand off to QZ Tray.
      const releasedIds: string[] = _.flatMap(
        results,
        (x: any) => (x?.ReleasedItemDetailIds ?? []) as string[],
      );
      if (releasedIds.length > 0) {
        this.uow.clearEntities(ItemDetail);
        const refreshed = await this.uow
          .createQuery(ItemDetail, "ItemDetails")
          .where({ id: { in: releasedIds } })
          .expand(["product.productType.manufacturer"])
          .execute();
        if (refreshed.length > 0) {
          const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels(refreshed);
          if (changes.length > 0) {
            await this.dbSaveService.saveSelectedChanges(changes);
          }

          // Safety net for printer jams / offline printer. Loop reprints the
          // whole batch (bin labels are irrelevant on Op D — released items
          // have no addons). See fill-from-inventory for the askYesNo vs
          // yesNoDialog rationale.
          const n = refreshed.length;
          const noun = n === 1 ? "barcode" : "barcodes";
          while (!(await this.dialogService.yesNoDialog("Printed OK?", `Did the ${n} ${noun} print correctly?`))) {
            await this.barcodeService.printItemDetailBarcodes(refreshed);
          }
        }
      }
      const anyEmail = results.some((x: any) => x?.EmailQueued);
      const emailNote = anyEmail
        ? (results.filter((x: any) => x?.EmailQueued).length === 1
            ? " · manufacturer email queued"
            : " · manufacturer emails queued")
        : "";
      const totalUnits = this.totalUnitsToCancel;
      this.toastr.success(
        `Cancelled ${totalUnits} ${totalUnits === 1 ? "unit" : "units"} ` +
        `across ${rows.length} ${rows.length === 1 ? "detail" : "details"}. ` +
        `Released ${totalReleased} allocated ${totalReleased === 1 ? "item" : "items"}, ` +
        `cancelled open qty on ${totalPo} PO ${totalPo === 1 ? "line" : "lines"}${emailNote}.` +
        (totalNotYetOrdered > 0
          ? ` ${totalNotYetOrdered} ${totalNotYetOrdered === 1 ? "unit was" : "units were"} not pulled or ordered yet.`
          : ""),
        "Cancel Order Details",
      );

      // Reset so the operator can search for another JO. A partial cancel
      // would benefit from a re-fetch to show updated ceilings, but a full-JO
      // cancel closes the JO — findJoHeader() would then toast an error on
      // that legitimate terminal state.
      this.reset();
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Cancel failed.";
      this.toastr.error(msg, "Cancel Order Details");
    }
  }

  reset() {
    this.joNumberInput = "";
    this.resetSelection();
    setTimeout(() => this.joSearchInput?.nativeElement?.focus(), 0);
  }

  private resetSelection() {
    this.selectedJoHeader = null;
    this.cancellableRows = [];
  }
}
